import { IAplosFileApiService } from "@/components/FileManager/services/AplosFileApiService";
import { AnalysisWizardInitializationArgs } from "../models/AnalysisWizardInitializationArgs";
import { AnalysisWizardSharedModel } from "../models/AnaylsisWizardSharedModel";
import { IDataCleaningApiService } from "@/components/DataCleaning/services/DataCleaningApiService";
import { GetAplosFileArgs } from "@/components/FileManager/models/GetAplosFileArgs";
import { GetRulesArgs } from "@/components/DataCleaning/models/GetRulesArgs";
import { AnalysisConfigurationFactory } from "./AnalysisConfigurationFactory";
import { InjectableService } from "@/shared/services/InjectableService";
import { GetExecutionStatusArgs } from "@/shared/Executions/models/GetExecutionStatusArgs";
import { GetExecutionConfigArgs } from "@/shared/Executions/models/GetExecutionConfigArgs";
import { AplosFile } from "@/components/FileManager/models/AplosFile";
import { AplosFileLineage } from "@/components/FileManager/models/AplosFileLineage";
import { IExecutionApiService } from "@/shared/Executions/services/ExecutionApiService";
import { CdnSchemaFactory } from "@/shared/CdnSchema/services/CdnSchemaFactory";
import { ICustomSchemaStore } from "@/shared/models/ICustomSchemaStore";
import { DataCleaningJsonParsingService } from "@/components/DataCleaning/services/DataCleaningJsonParsingService";
import { CdnSchemaType } from "@/shared/CdnSchema/models/CdnSchemaType";
import { AnalysisConfigJsonParsingService } from "@/components/AnalysisConfig/services/AnalysisConfigJsonParsingService";
import { CalculationsJsonParsingService } from "@/components/CustomCalculations/services/CalculationsJsonParsingService";
import { TablesJsonParsingService } from "@/components/Tables/services/TablesJsonParsingService";
import { PlottingJsonParsingService } from "@/components/Plotting/services/PlottingJsonParsingService";
import { CustomReportsJsonParsingService } from "@/components/CustomReports/services/CustomReportsJsonParsingService";



export class AnalysisWizardInitializationService extends InjectableService {

    static readonly injectableKey = 'AnalysisWizardInitializationService';

    get injectableKey() {
        return (this.constructor as typeof AnalysisWizardInitializationService).injectableKey;
    }

    constructor(private readonly _aplosFileApiService: IAplosFileApiService,
        private readonly _aplosDataCleaningApiService: IDataCleaningApiService,
        private readonly _executionApiService: IExecutionApiService,
        private readonly _cdnSchemaService: CdnSchemaFactory,
        private readonly _dataCleaningJsonParsingService: DataCleaningJsonParsingService,
        private readonly _analysisConfigJsonParsingService: AnalysisConfigJsonParsingService,
        private readonly _calculationsJsonParsingService: CalculationsJsonParsingService,
        private readonly _tablesJsonParsingService: TablesJsonParsingService,
        private readonly _plottingJsonParsingService: PlottingJsonParsingService,
        private readonly _customReportsJsonParsingService: CustomReportsJsonParsingService,

    ) {
        super();
    }

    public async intializeSharedModelFromArgs(tenantId: string, userId: string, sharedModel: AnalysisWizardSharedModel,
        initializationArgs: AnalysisWizardInitializationArgs, customSchemaStore: ICustomSchemaStore): Promise<AnalysisWizardSharedModel> {

        if (tenantId == null) throw Error("tenantId is required");
        if (userId == null) throw Error("userId is required");
        if (sharedModel == null) throw Error("sharedModel is required");
        if (initializationArgs == null) throw Error("initializationArgs is required");
        if (customSchemaStore == null) throw Error("customSchemaStore is required");

        // Collect promises as Promise<void> so different API return types can be handled
        // inline and mapped into the sharedModel before Promise.all resolves.
        const promises: Array<Promise<void>> = [];

        if (initializationArgs.analysisFileId != null) {
            const args = new GetAplosFileArgs({
                tenantId: tenantId as string,
                userId: userId as string,
                fileId: initializationArgs.analysisFileId!
            });
            const response = await this._aplosFileApiService.getFile(args);
            const file = response.data as AplosFile;
            if (initializationArgs.cleanedFileId == null && file.lineage === AplosFileLineage.Cleaned) {
                // set cleanedFileId so the cleanedFileId block below will run its fetches
                initializationArgs.cleanedFileId = file.id;
                const getOriginalFileArgs = new GetAplosFileArgs({ tenantId, userId, fileId: file.parentId! });
                const originalInputResponse = await this._aplosFileApiService.getFile(getOriginalFileArgs);
                sharedModel.inputFile = originalInputResponse.data;
                sharedModel.cleanedFile = file;
            }
            else {
                sharedModel.inputFile = response.data;
            }
        }

        if (initializationArgs.cleanedFileId) {

            promises.push(
                (async () => {
                    // Get cleaned file if not already obtained previously
                    if (sharedModel.cleanedFile == null) {
                        const args = new GetAplosFileArgs({
                            tenantId: tenantId as string,
                            userId: userId as string,
                            fileId: initializationArgs.cleanedFileId!
                        });
                        const response = await this._aplosFileApiService.getFile(args);
                        sharedModel.cleanedFile = response.data;
                    }

                    // If no execution Id new up analysis configuraton
                    if (initializationArgs.analysisExecutionId == null) {
                        sharedModel.analysisConfig = AnalysisConfigurationFactory.createNewAnalysisConfiguration()
                    }
                })()
            )

            promises.push(
                (async () => {
                    const args = new GetRulesArgs({
                        tenantId: tenantId as string,
                        userId: userId as string,
                        fileId: initializationArgs.cleanedFileId!
                    });
                    const flatRules = await this._aplosDataCleaningApiService.getRules(args);

                    const ruleSchemas = await this._cdnSchemaService.loadAvailableSchemasForCdnSchemaType(CdnSchemaType.RuleSchema, customSchemaStore);
                    const parseResult = this._dataCleaningJsonParsingService.tryParseEntitiesFromJson(JSON.stringify(flatRules), ruleSchemas);
                    if (!parseResult.valid) throw Error("Error loading and initializing existing rules!");
                    sharedModel.rules = parseResult.parsedValue
                })()
            )
        }

        // This can represent a pk parameters calculation with additional execution steps
        if (initializationArgs.analysisExecutionId) {

            promises.push(
                (async () => {
                    const args = new GetExecutionConfigArgs({
                        userId: userId as string,
                        tenantId: tenantId as string,
                        executionId: initializationArgs.analysisExecutionId!
                    });

                    const executionConfigResponse = await this._executionApiService.getExecutionConfig(args);

                    const analysisConfigSchemas = await this._cdnSchemaService.loadAvailableSchemasForCdnSchemaType(CdnSchemaType.AnalysisConfigSchema, customSchemaStore);
                    const parseResult = this._analysisConfigJsonParsingService.tryParseEntitiesFromJson(JSON.stringify(executionConfigResponse.data?.calculation), analysisConfigSchemas);
                    if (!parseResult.valid) throw Error("Error loading and initializing existing PK Analysis config!");

                    sharedModel.analysisConfig = parseResult.parsedValue
                })()
            )

            promises.push(
                (async () => {
                    const args = new GetExecutionStatusArgs({
                        userId: userId as string,
                        tenantId: tenantId as string,
                        executionId: initializationArgs.analysisExecutionId!
                    });

                    const getExecutionStatusResponse = await this._executionApiService.getExecutionStatus(args);
                    sharedModel.analysisExecution = getExecutionStatusResponse.data;
                })()
            )

        }

        if (initializationArgs.customCalculationsExecutionId) {

            promises.push(
                (async () => {
                    const args = new GetExecutionStatusArgs({
                        userId: userId as string,
                        tenantId: tenantId as string,
                        executionId: initializationArgs.customCalculationsExecutionId!
                    });

                    const customCaculationsConfigResponse = await this._executionApiService.getExecutionConfig(args);


                    const calculationSchemas = await this._cdnSchemaService.loadAvailableSchemasForCdnSchemaType(CdnSchemaType.CalculationSchema, customSchemaStore);
                    const parseResult = this._calculationsJsonParsingService.tryParseEntitiesFromJson(JSON.stringify(customCaculationsConfigResponse.data?.customCalculations), calculationSchemas);
                    if (!parseResult.valid) throw Error("Error loading and initializing existing Custom Caluclations config!");


                    sharedModel.customCalculations = parseResult.parsedValue?.calculations;
                    sharedModel.customCalculationsSort = parseResult.parsedValue?.output?.sort;
                })()
            )

            promises.push(
                (async () => {
                    const args = new GetExecutionStatusArgs({
                        userId: userId as string,
                        tenantId: tenantId as string,
                        executionId: initializationArgs.customCalculationsExecutionId!
                    });

                    const getExecutionStatusResponse = await this._executionApiService.getExecutionStatus(args);
                    sharedModel.customCalculationsExecution = getExecutionStatusResponse.data;
                })()
            )

        }

        if (initializationArgs.postProcessingExecutionId) {

            promises.push(
                (async () => {
                    const args = new GetExecutionStatusArgs({
                        userId: userId as string,
                        tenantId: tenantId as string,
                        executionId: initializationArgs.postProcessingExecutionId!
                    });

                    const postProcessingConfigResponse = await this._executionApiService.getExecutionConfig(args);

                    const tableSchemas = await this._cdnSchemaService.loadAvailableSchemasForCdnSchemaType(CdnSchemaType.TableSchema, customSchemaStore);
                    const plotSchemas = await this._cdnSchemaService.loadAvailableSchemasForCdnSchemaType(CdnSchemaType.PlotSchema, customSchemaStore);

                    const tables = postProcessingConfigResponse.data?.customTables?.tables;
                    if (tables) {
                        const tableParseResult = this._tablesJsonParsingService.tryParseEntitiesFromJson(JSON.stringify(postProcessingConfigResponse.data?.customTables?.tables), tableSchemas);
                        if (!tableParseResult.valid) throw Error("Error loading and initializing existing tables post processing config!");
                        sharedModel.tables = tableParseResult.parsedValue;
                    }

                    const plots = postProcessingConfigResponse.data?.customPlots?.plots;
                    if (plots) {
                        const plotParseResult = this._plottingJsonParsingService.tryParseEntitiesFromJson(JSON.stringify(postProcessingConfigResponse.data?.customPlots?.plots), plotSchemas);
                        if (!plotParseResult.valid) throw Error("Error loading and initializing existing plots post processing config!");
                        sharedModel.plots = plotParseResult.parsedValue;
                    }

                })()
            )

            promises.push(
                (async () => {
                    const args = new GetExecutionStatusArgs({
                        userId: userId as string,
                        tenantId: tenantId as string,
                        executionId: initializationArgs.postProcessingExecutionId!
                    });

                    const getExecutionStatusResponse = await this._executionApiService.getExecutionStatus(args);
                    sharedModel.postProcessingExecution = getExecutionStatusResponse.data;
                })()
            )

        }

        if (initializationArgs.customReportsExecutionId) {

            promises.push(
                (async () => {
                    const args = new GetExecutionStatusArgs({
                        userId: userId as string,
                        tenantId: tenantId as string,
                        executionId: initializationArgs.customReportsExecutionId!
                    });

                    const customReportsConfigResponse = await this._executionApiService.getExecutionConfig(args);

                    const customReportSchemas = await this._cdnSchemaService.loadAvailableSchemasForCdnSchemaType(CdnSchemaType.CustomReportSchema, customSchemaStore);
                    const parseResult = this._customReportsJsonParsingService.tryParseEntitiesFromJson(JSON.stringify(customReportsConfigResponse.data?.customReports?.reports), customReportSchemas);
                    if (!parseResult.valid) throw Error("Error loading and initializing existing report!");

                    sharedModel.customReports = parseResult.parsedValue;
                })()
            )

            promises.push(
                (async () => {
                    const args = new GetExecutionStatusArgs({
                        userId: userId as string,
                        tenantId: tenantId as string,
                        executionId: initializationArgs.customReportsExecutionId!
                    });

                    const getExecutionStatusResponse = await this._executionApiService.getExecutionStatus(args);
                    sharedModel.customReportsExecution = getExecutionStatusResponse.data;
                })()
            )

        }

        await Promise.all(promises);

        return sharedModel;
    }


}